import { useEffect, useState } from "react";
import { Crown, Fire, Gift, InstagramLogo, Martini, QrCode, ShareNetwork, Star, Ticket, UsersThree, WhatsappLogo, ChatCircleText } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { TIERS } from "../../lib/tiers";

const EARN_WAYS = [
  { icon: QrCode, label: "Scan in at a venue", pts: "+250" },
  { icon: Ticket, label: "Attend an event", pts: "+250" },
  { icon: UsersThree, label: "Refer a friend who scans in", pts: "+500" },
  { icon: Star, label: "Review a venue", pts: "+50" },
  { icon: Fire, label: "Nightlife streaks", pts: "+500" },
  { icon: Martini, label: "Discover new spots", pts: "+150" },
];

const safeGet = (path, fallback) => API.get(path).then((r) => r.data).catch(() => fallback);

export default function Rewards() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [referral, setReferral] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claiming, setClaiming] = useState("");

  const load = () => Promise.all([
    safeGet("/me/rewards", null),
    safeGet("/referrals/me", null),
    safeGet("/rewards/catalog", []),
    safeGet("/rewards/my-claims", []),
  ]).then(([rewardsData, referralData, catalogData, claimsData]) => {
    setData(rewardsData);
    setReferral(referralData);
    setCatalog(Array.isArray(catalogData) ? catalogData : []);
    setClaims(Array.isArray(claimsData) ? claimsData : []);
  });

  useEffect(() => { load(); }, []);

  if (!data) return <div className="px-5 pt-16 text-center text-sm text-zinc-500">Loading your status...</div>;

  const referralUrl = referral ? `${window.location.origin}${referral.referral_path}` : "";
  const referralMessage = referral
    ? `Join me on NITEPASS — Your Pass to the Night. Use my code ${referral.referral_code}: ${referralUrl}`
    : "";
  const progress = data.next_threshold
    ? Math.min(100, Math.round(((data.points) / data.next_threshold) * 100))
    : 100;

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed — long-press the link to share it");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) return copyReferral();
    try {
      await navigator.share({ title: "NITEPASS", text: referralMessage, url: referralUrl });
    } catch {}
  };

  const shareInstagram = async () => {
    try {
      await navigator.clipboard.writeText(referralMessage);
      toast.success("Referral message copied — paste it in Instagram");
      window.open("https://www.instagram.com/direct/inbox/", "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not copy the referral message");
    }
  };

  const claimReward = async (reward) => {
    setClaiming(reward.id);
    try {
      const { data: result } = await API.post(`/rewards/${reward.id}/claim`);
      toast.success(`Reward claimed — show code ${result.claim.code}`);
      await Promise.all([load(), refreshUser()]);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setClaiming("");
    }
  };

  return (
    <div data-testid="rewards-page" className="px-5 pt-8">
      <div className="neon-overline">NITE STATUS</div>
      <h1 className="mt-1 font-display text-3xl font-black">Rewards that travel<span className="text-neon-violet">.</span></h1>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="relative mt-6 overflow-hidden rounded-3xl border border-neon-purple/50 bg-[#0d0716] p-6">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-neon-purple/30 blur-3xl" />
        <div className="flex items-center justify-between">
          <div>
            <div className="neon-overline !text-neon-purple">Current status</div>
            <div className="mt-1 font-display text-4xl font-black text-glow-gold" data-testid="status-tier">{data.tier}</div>
          </div>
          <Crown size={48} weight="duotone" className="text-neon-yellow" />
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-xs font-bold">
            <span className="text-neon-yellow" data-testid="status-points">{data.points} NITE POINTS</span>
            {data.next_tier && <span className="text-zinc-500">{data.next_threshold - data.points} to {data.next_tier}</span>}
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 1 }}
              className="h-full rounded-full bg-gradient-to-r from-neon-gold to-neon-purple" />
          </div>
        </div>
        <div className="mt-5 flex justify-between">
          {TIERS.map((t) => (
            <div key={t.name} className={`text-center text-[10px] font-bold uppercase tracking-wider ${data.points >= t.threshold ? "text-neon-gold" : "text-zinc-600"}`}>
              <div className={`mx-auto mb-1 h-2.5 w-2.5 rounded-full ${data.points >= t.threshold ? "bg-neon-gold shadow-glow-gold" : "bg-zinc-700"}`} />
              {t.name}
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-zinc-500">Cross-venue loyalty: every verified check-in counts everywhere. {data.total_checkins} total check-ins.</p>
      </motion.div>

      {referral && (
        <section className="mt-8" data-testid="referral-panel">
          <div className="relative overflow-hidden rounded-3xl border border-neon-gold/40 bg-[#100d04] p-5">
            <div className="absolute -left-12 top-0 h-36 w-36 rounded-full bg-neon-gold/20 blur-3xl" />
            <div className="flex items-center gap-2">
              <ShareNetwork size={18} weight="fill" className="text-neon-gold" />
              <h2 className="font-display text-base font-black">INVITE FRIENDS · EARN 500</h2>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">
              Share your link or let a friend scan this referral QR. When they complete their first venue scan, you get +500 NITE POINTS.
            </p>
            <div className="mt-4 flex items-center gap-4">
              <div className="rounded-2xl bg-white p-3 shadow-glow-gold" data-testid="referral-qr">
                <QRCodeSVG value={referralUrl} size={112} bgColor="#ffffff" fgColor="#050505" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Your code</div>
                <div className="font-display text-2xl font-black text-neon-gold" data-testid="referral-code">{referral.referral_code}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-center text-[11px] font-bold">
                  <div className="rounded-xl bg-white/5 px-2 py-2"><span className="text-neon-violet">{referral.invited}</span> joined</div>
                  <div className="rounded-xl bg-white/5 px-2 py-2"><span className="text-neon-yellow">{referral.points_earned}</span> pts earned</div>
                </div>
                <button data-testid="copy-referral-btn" onClick={copyReferral}
                  className="mt-3 w-full rounded-full bg-neon-gold py-2.5 text-xs font-black text-black shadow-glow-gold transition-transform duration-150 hover:-translate-y-0.5">
                  Copy Invite Link
                </button>
                <div className="mt-2 grid grid-cols-4 gap-1.5" data-testid="referral-share-actions">
                  <button data-testid="share-native-btn" onClick={shareNative}
                    className="rounded-xl border border-white/10 bg-white/5 px-2 py-2 text-[10px] font-bold text-zinc-300 hover:border-neon-gold/40">
                    <ShareNetwork size={14} className="mx-auto mb-1" /> Share
                  </button>
                  <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(referralMessage)}`} target="_blank" rel="noreferrer"
                    className="rounded-xl border border-white/10 bg-white/5 px-2 py-2 text-center text-[10px] font-bold text-zinc-300 hover:border-neon-green/40">
                    <WhatsappLogo size={14} className="mx-auto mb-1 text-neon-green" /> WhatsApp
                  </a>
                  <a data-testid="share-imessage-btn" href={`sms:?&body=${encodeURIComponent(referralMessage)}`}
                    className="rounded-xl border border-white/10 bg-white/5 px-2 py-2 text-center text-[10px] font-bold text-zinc-300 hover:border-neon-violet/40">
                    <ChatCircleText size={14} className="mx-auto mb-1 text-neon-violet" /> iMessage
                  </a>
                  <button data-testid="share-instagram-btn" onClick={shareInstagram}
                    className="rounded-xl border border-white/10 bg-white/5 px-2 py-2 text-[10px] font-bold text-zinc-300 hover:border-neon-yellow/40">
                    <InstagramLogo size={14} className="mx-auto mb-1 text-neon-yellow" /> Instagram
                  </button>
                </div>
              </div>
            </div>
            {referral.referrals.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="referral-list">
                {referral.referrals.slice(0, 4).map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-xs">
                    <span><span className="font-bold">{r.name}</span> <span className="text-neon-violet">{r.handle}</span></span>
                    <span className={r.status === "rewarded" ? "font-bold text-neon-green" : "text-zinc-500"}>
                      {r.status === "rewarded" ? `+${r.points_awarded}` : "Joined"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="mt-8" data-testid="nitepass-rewards">
        <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold"><Gift size={17} className="text-neon-gold" /> NITEPASS REWARDS</h2>
        <div className="space-y-3">
          {catalog.map((reward) => {
            const soldOut = reward.quantity_remaining === 0;
            const unaffordable = data.points < reward.point_cost;
            return (
              <div key={reward.id} className="card-neon overflow-hidden" data-testid={`nitepass-reward-${reward.id}`}>
                {reward.image_url && <img src={mediaUrl(reward.image_url)} alt={reward.title} className="h-28 w-full object-cover" />}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-sm font-black">{reward.title}</div>
                      {reward.description && <p className="mt-1 text-xs text-zinc-400">{reward.description}</p>}
                    </div>
                    <div className="rounded-full bg-neon-yellow/10 px-3 py-1 text-xs font-black text-neon-yellow">{reward.point_cost} PTS</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    <span>{reward.venue_name || "Any participating venue"}</span>
                    {reward.quantity_remaining !== null && <span>· {reward.quantity_remaining} left</span>}
                    {reward.expires_at && <span>· Ends {new Date(reward.expires_at).toLocaleDateString()}</span>}
                  </div>
                  <button data-testid={`claim-reward-${reward.id}`} onClick={() => claimReward(reward)}
                    disabled={claiming === reward.id || soldOut || unaffordable}
                    className="btn-neon mt-4 w-full !py-2.5 text-sm disabled:opacity-40">
                    {soldOut ? "Sold Out" : unaffordable ? `Need ${reward.point_cost - data.points} more pts` : claiming === reward.id ? "Claiming..." : "Claim Reward"}
                  </button>
                </div>
              </div>
            );
          })}
          {catalog.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center text-sm text-zinc-500" data-testid="no-nitepass-rewards">
              New NITEPASS rewards are coming soon. Keep stacking points.
            </div>
          )}
        </div>
      </section>

      {claims.length > 0 && (
        <section className="mt-8" data-testid="my-reward-claims">
          <h2 className="mb-3 font-display text-base font-bold">MY CLAIMED REWARDS</h2>
          <div className="space-y-2">
            {claims.map((claim) => (
              <div key={claim.id} className="rounded-2xl border border-neon-green/30 bg-neon-green/5 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-black">{claim.title}</div>
                    <div className="mt-0.5 text-[11px] text-zinc-500">Show this code at the venue</div>
                  </div>
                  <div className="rounded-full bg-black px-3 py-1.5 font-mono text-xs font-black text-neon-green" data-testid={`claim-code-${claim.id}`}>{claim.code}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-8">
        <h2 className="mb-3 font-display text-base font-bold">WAYS TO EARN</h2>
        <div className="grid grid-cols-2 gap-3">
          {EARN_WAYS.map((w) => (
            <div key={w.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <w.icon size={20} weight="duotone" className="text-neon-violet" />
              <div className="mt-2 text-xs font-semibold text-zinc-300">{w.label}</div>
              <div className="mt-1 text-sm font-black text-neon-yellow">{w.pts}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold"><Gift size={17} className="text-neon-gold" /> VENUE REWARDS</h2>
        <div className="space-y-3" data-testid="venue-rewards">
          {data.venue_rewards.length === 0 && (
            <p className="py-3 text-sm text-zinc-500">Scan in at a partner venue to start building visit rewards.</p>
          )}
          {data.venue_rewards.map((vr) => (
            <div key={vr.venue_id} className="card-neon p-4">
              <div className="flex items-center justify-between">
                <div className="font-display text-sm font-black">{vr.venue_name}</div>
                <span className="text-[11px] font-bold text-zinc-500">{vr.visits} visits</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {vr.milestones.map((m) => (
                  <span key={m.visits}
                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${m.reached ? "bg-neon-green/15 text-neon-green" : "bg-white/5 text-zinc-500"}`}>
                    {m.visits} {m.visits === 1 ? "visit" : "visits"} · {m.reward}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
